import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
  Tabs,
  Descriptions,
  Statistic,
  Row,
  Col,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DollarOutlined, CheckOutlined, CloseOutlined, FilePdfOutlined, PlusCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useAuth } from '../contexts/AuthContext';
import { parseComprobantesGasto, buildComprobanteGastoUrl } from '../utils/comprobanteGasto';

const { TextArea } = Input;

const CajaChica = () => {
  const { user } = useAuth();
  const isMainAdmin = user && user.rol === 'admin' && !user.empresa_id;
  const isAdmin = user && user.rol === 'admin';

  const [cajasChica, setCajasChica] = useState([]);
  const [gastos, setGastos] = useState([]);
  const [cobros, setCobros] = useState([]);
  const [ajustesSaldo, setAjustesSaldo] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [movimientosModalVisible, setMovimientosModalVisible] = useState(false);
  const [ajustarSaldoModalVisible, setAjustarSaldoModalVisible] = useState(false);
  const [historialAjustesModalVisible, setHistorialAjustesModalVisible] = useState(false);
  const [cajaAjustar, setCajaAjustar] = useState(null);
  const [historialAjustes, setHistorialAjustes] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [editingCaja, setEditingCaja] = useState(null);
  const [selectedCaja, setSelectedCaja] = useState(null);
  const [ajustesCaja, setAjustesCaja] = useState([]);
  const [loadingAjustesCaja, setLoadingAjustesCaja] = useState(false);
  const [form] = Form.useForm();
  const [gastosForm] = Form.useForm();
  const [ajustarSaldoForm] = Form.useForm();

  // Separar cajas chicas por tipo y estado
  const cajasEfectivo = cajasChica.filter(c => c.tipo === 'efectivo' && c.estado === 'en_curso');
  const cajasTarjeta = cajasChica.filter(c => c.tipo === 'tarjeta' && c.estado === 'en_curso');
  const cajasEfectivoLiquidadas = cajasChica.filter(c => c.tipo === 'efectivo' && c.estado === 'liquidado');
  const cajasTarjetaLiquidadas = cajasChica.filter(c => c.tipo === 'tarjeta' && c.estado === 'liquidado');

  useEffect(() => {
    fetchCajasChica();
    fetchGastos();
    fetchCobros();
    fetchPersonal();
  }, []);

  const fetchCajasChica = async () => {
    setLoading(true);
    try {
      const response = await api.get('/caja-chica');
      const cajas = response.data || [];
      setCajasChica(cajas);

      // Obtener ajustes de todas las cajas chicas
      if (cajas.length > 0) {
        try {
          const ajustesPromises = cajas.map(caja =>
            api.get(`/caja-chica/${caja.id}/historial-ajustes`).catch(() => ({ data: [] }))
          );
          const ajustesResponses = await Promise.all(ajustesPromises);
          const todosLosAjustes = ajustesResponses.flatMap(res => Array.isArray(res.data) ? res.data : []);
          setAjustesSaldo(todosLosAjustes);
        } catch (error) {
          console.error('Error al cargar ajustes de saldo');
          setAjustesSaldo([]);
        }
      }
    } catch (error) {
      message.error('Error al cargar cajas chica');
    } finally {
      setLoading(false);
    }
  };

  const fetchGastos = async () => {
    try {
      const response = await api.get('/gastos-caja-chica');
      setGastos(response.data);
    } catch (error) {
      message.error('Error al cargar gastos');
    }
  };

  const fetchCobros = async () => {
    try {
      const response = await api.get('/cobros-caja-chica');
      setCobros(response.data);
    } catch (error) {
      message.error('Error al cargar cobros');
    }
  };

  const fetchPersonal = async () => {
    try {
      const response = await api.get('/personal');
      setPersonal(response.data.filter(p => p.estado === 'activo'));
    } catch (error) {
      console.error('Error al cargar personal');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingCaja) {
        // Solo permitir editar observaciones
        await api.put(`/caja-chica/${editingCaja.id}`, {
          observaciones: values.observaciones
        });
        message.success('Observaciones actualizadas exitosamente');
      } else {
        // Validar que el personal no tenga una caja chica del mismo tipo en curso
        const tipo = values.tipo || 'efectivo';
        const cajaEnCurso = cajasChica.find(
          c => c.personal_id === values.personal_id &&
            c.tipo === tipo &&
            c.estado === 'en_curso'
        );
        if (cajaEnCurso) {
          message.error(`Este operario ya tiene una caja chica de ${tipo} en curso. Debe liquidarla antes de asignar una nueva.`);
          return;
        }

        await api.post('/caja-chica', values);
        message.success('Caja chica asignada exitosamente');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingCaja(null);
      fetchCajasChica();
      fetchCobros();
      fetchCobros();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleAjustarSaldo = (record) => {
    setCajaAjustar(record);
    ajustarSaldoForm.setFieldsValue({
      monto: 0,
    });
    setAjustarSaldoModalVisible(true);
  };

  const handleAjustarSaldoSubmit = async (values) => {
    if (!cajaAjustar) return;

    try {
      await api.put(`/caja-chica/${cajaAjustar.id}/ajustar-saldo`, {
        monto: values.monto || 0,
        observaciones: values.observaciones || null,
      });
      message.success('Saldo ajustado exitosamente');
      setAjustarSaldoModalVisible(false);
      ajustarSaldoForm.resetFields();
      setCajaAjustar(null);
      fetchCajasChica();
      fetchCajasChica();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleVerHistorialAjustes = async (record) => {
    setCajaAjustar(record);
    setLoadingHistorial(true);
    try {
      const response = await api.get(`/caja-chica/${record.id}/historial-ajustes`);
      setHistorialAjustes(response.data);
      setHistorialAjustesModalVisible(true);
    } catch (error) {
      message.error('Error al cargar historial de ajustes');
    } finally {
      setLoadingHistorial(false);
    }
  };

  const [liquidarModalVisible, setLiquidarModalVisible] = useState(false);
  const [cajaLiquidar, setCajaLiquidar] = useState(null);
  const [liquidarForm] = Form.useForm();

  const handleEdit = (record) => {
    setEditingCaja(record);
    form.setFieldsValue({
      personal_id: record.personal_id?.toString(),
      tipo: record.tipo,
      observaciones: record.observaciones,
    });
    setModalVisible(true);
  };

  const handleDelete = async (record) => {
    try {
      await api.delete(`/caja-chica/${record.id}`);
      message.success('Caja chica eliminada exitosamente');
      fetchCajasChica();
      fetchCobros();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleLiquidar = (record) => {
    setCajaLiquidar(record);
    // Para tarjeta, no se muestra devolución, pero se inicializa en 0
    liquidarForm.setFieldsValue({
      devolucion: record.tipo === 'tarjeta' ? 0 : (record.devolucion || 0),
    });
    setLiquidarModalVisible(true);
  };

  // Calcular montos disponibles para la caja chica a liquidar
  const calcularMontosDisponibles = (caja) => {
    if (!caja) return { efectivoDisponible: 0, tarjetaDisponible: 0 };

    if (caja.tipo === 'efectivo') {
      // Obtener gastos de esta caja chica de efectivo
      const gastosCaja = gastos.filter(g => g.caja_chica_id === caja.id);
      const gastosEfectivoAprobados = gastosCaja.filter(
        g => (g.tipo_pago === 'efectivo' || !g.tipo_pago) && g.estado === 'aprobado'
      );
      const totalGastosEfectivo = gastosEfectivoAprobados.reduce(
        (sum, g) => sum + parseFloat(g.monto || 0), 0
      );
      const cobrosCaja = cobros.filter(c => c.caja_chica_id === caja.id);
      const totalCobros = cobrosCaja.reduce((sum, c) => sum + parseFloat(c.monto || 0), 0);

      // Obtener ajustes de saldo para esta caja chica de efectivo
      const ajustesCaja = ajustesSaldo.filter(a => a.caja_chica_id === caja.id && a.tipo_caja === 'efectivo');
      const ajustesAgregar = ajustesCaja
        .filter(a => a.tipo_ajuste === 'agregar')
        .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
      const ajustesQuitar = ajustesCaja
        .filter(a => a.tipo_ajuste === 'quitar')
        .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
      const ajustesNeto = ajustesAgregar - ajustesQuitar;

      const efectivoDisponible = parseFloat(caja.monto_asignado || 0) - totalGastosEfectivo + totalCobros + ajustesNeto;
      return { efectivoDisponible, tarjetaDisponible: 0 };
    } else {
      // Obtener gastos de esta caja chica de tarjeta
      const gastosCaja = gastos.filter(g => g.caja_chica_id === caja.id);
      const gastosTarjetaAprobados = gastosCaja.filter(
        g => g.tipo_pago === 'tarjeta' && g.estado === 'aprobado'
      );
      const totalGastosTarjeta = gastosTarjetaAprobados.reduce(
        (sum, g) => sum + parseFloat(g.monto || 0), 0
      );

      // Obtener ajustes de saldo para esta caja chica de tarjeta
      const ajustesCaja = ajustesSaldo.filter(a => a.caja_chica_id === caja.id && a.tipo_caja === 'tarjeta');
      const ajustesAgregar = ajustesCaja
        .filter(a => a.tipo_ajuste === 'agregar')
        .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
      const ajustesQuitar = ajustesCaja
        .filter(a => a.tipo_ajuste === 'quitar')
        .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
      const ajustesNeto = ajustesAgregar - ajustesQuitar;

      const tarjetaDisponible = parseFloat(caja.tarjeta_combustible || 0) - totalGastosTarjeta + ajustesNeto;
      return { efectivoDisponible: 0, tarjetaDisponible };
    }
  };

  const handleLiquidarSubmit = async (values) => {
    if (!cajaLiquidar) return;

    try {
      // Para tarjeta, no se envía devolución (siempre será 0)
      const data = cajaLiquidar.tipo === 'tarjeta'
        ? { devolucion: 0 }
        : { devolucion: values.devolucion || 0 };

      await api.put(`/caja-chica/${cajaLiquidar.id}/liquidar`, data);
      message.success('Caja chica liquidada exitosamente');
      setLiquidarModalVisible(false);
      liquidarForm.resetFields();
      setCajaLiquidar(null);
      fetchCajasChica();
      fetchCajasChica();
      fetchCobros();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleVerMovimientos = async (record) => {
    setSelectedCaja(record);
    setMovimientosModalVisible(true);
    setLoadingAjustesCaja(true);
    try {
      // Obtener ajustes solo de la caja chica seleccionada
      const ajustesResponse = await api.get(`/caja-chica/${record.id}/historial-ajustes`).catch(() => ({ data: [] }));
      const ajustes = Array.isArray(ajustesResponse.data) ? ajustesResponse.data : [];
      setAjustesCaja(ajustes);
    } catch (error) {
      message.error('Error al cargar ajustes de saldo');
      setAjustesCaja([]);
    } finally {
      setLoadingAjustesCaja(false);
    }
  };

  const handleExportarPDF = async (record) => {
    try {
      message.loading({ content: 'Generando PDF...', key: 'export' });

      // Obtener solo los gastos y cobros de la caja seleccionada
      const gastosCaja = gastos.filter(g => g.caja_chica_id === record.id);
      const cobrosCaja = cobros.filter(c => c.caja_chica_id === record.id);

      // Traer ajustes de saldo solo de la caja seleccionada
      let ajustes = [];
      try {
        const ajustesResponse = await api.get(`/caja-chica/${record.id}/historial-ajustes`).catch(() => ({ data: [] }));
        ajustes = Array.isArray(ajustesResponse.data) ? ajustesResponse.data : [];
      } catch (e) {
        // No bloquear el PDF si falla el historial
        ajustes = [];
      }

      // Separar gastos por tipo de pago (solo de esta caja)
      const gastosEfectivo = gastosCaja.filter(g => g.tipo_pago === 'efectivo' || !g.tipo_pago);
      const gastosTarjeta = gastosCaja.filter(g => g.tipo_pago === 'tarjeta');

      // Solo contar gastos aprobados
      const gastosEfectivoAprobados = gastosEfectivo.filter(g => g.estado === 'aprobado');
      const gastosTarjetaAprobados = gastosTarjeta.filter(g => g.estado === 'aprobado');

      const totalGastosEfectivo = gastosEfectivoAprobados.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);
      const totalGastosTarjeta = gastosTarjetaAprobados.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);

      const totalCobros = cobrosCaja.reduce((sum, c) => sum + parseFloat(c.monto || 0), 0);

      // Separar ajustes por tipo de caja (solo de esta caja)
      const ajustesEfectivo = ajustes.filter(a => a.tipo_caja === 'efectivo');
      const ajustesTarjeta = ajustes.filter(a => a.tipo_caja === 'tarjeta');

      // Calcular ajustes netos para efectivo
      const ajustesEfectivoAgregar = ajustesEfectivo
        .filter(a => a.tipo_ajuste === 'agregar')
        .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
      const ajustesEfectivoQuitar = ajustesEfectivo
        .filter(a => a.tipo_ajuste === 'quitar')
        .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
      const totalAjustesEfectivoNeto = ajustesEfectivoAgregar - ajustesEfectivoQuitar;

      // Calcular ajustes netos para tarjeta
      const ajustesTarjetaAgregar = ajustesTarjeta
        .filter(a => a.tipo_ajuste === 'agregar')
        .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
      const ajustesTarjetaQuitar = ajustesTarjeta
        .filter(a => a.tipo_ajuste === 'quitar')
        .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
      const totalAjustesTarjetaNeto = ajustesTarjetaAgregar - ajustesTarjetaQuitar;

      // Calcular disponibles usando datos de la caja seleccionada
      const efectivoDisponible = record.tipo === 'efectivo'
        ? parseFloat(record.monto_asignado || 0) - totalGastosEfectivo + totalCobros + totalAjustesEfectivoNeto
        - (record.estado === 'liquidado' ? parseFloat(record.devolucion || 0) : 0)
        : 0;
      const tarjetaDisponible = record.tipo === 'tarjeta'
        ? parseFloat(record.tarjeta_combustible || 0) - totalGastosTarjeta + totalAjustesTarjetaNeto
        : 0;

      // Crear un elemento temporal para el PDF
      const div = document.createElement('div');
      div.style.width = '210mm';
      div.style.padding = '20mm';
      div.style.fontFamily = 'Arial, sans-serif';
      div.style.backgroundColor = '#fff';
      div.style.position = 'absolute';
      div.style.left = '-9999px';
      document.body.appendChild(div);

      // Contenido del PDF
      div.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="margin: 0; font-size: 24px; color: #1890ff;">Resumen de Caja Chica</h1>
          <p style="margin: 5px 0; font-size: 14px; color: #666;">Fecha de generación: ${dayjs().format('DD/MM/YYYY HH:mm')}</p>
        </div>
        
        <div style="margin-bottom: 30px; border: 1px solid #d9d9d9; padding: 15px; border-radius: 4px;">
          <h2 style="margin: 0 0 15px 0; font-size: 18px; color: #333;">Información del Operario</h2>
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold; width: 40%;">Nombre:</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0;">${record.personal_nombre || '-'}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold;">Documento:</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0;">${record.personal_documento || '-'}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold;">Cargo:</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0;">${record.personal_cargo || '-'}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold;">Estado:</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0;">${record.estado === 'liquidado' ? 'Liquidado' : 'En Curso'}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold;">Fecha Apertura:</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0;">${record.fecha_apertura ? dayjs(record.fecha_apertura).format('DD/MM/YYYY') : '-'}</td>
            </tr>
            ${record.fecha_liquidacion ? `
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold;">Fecha Liquidación:</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0;">${dayjs(record.fecha_liquidacion).format('DD/MM/YYYY')}</td>
            </tr>
            ` : ''}
          </table>
        </div>
        
        ${record.tipo === 'efectivo' ? `
        <div style="margin-bottom: 30px; border: 1px solid #d9d9d9; padding: 15px; border-radius: 4px;">
          <h2 style="margin: 0 0 15px 0; font-size: 18px; color: #333;">Resumen Financiero - Efectivo</h2>
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold; width: 50%;">Monto Asignado:</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; color: #1890ff; font-size: 16px;">S/ ${parseFloat(record.monto_asignado || 0).toFixed(2)}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold;">Gastos en Efectivo:</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; color: #cf1322; font-size: 16px;">S/ ${totalGastosEfectivo.toFixed(2)}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold;">Cobros:</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; color: #1890ff; font-size: 16px;">S/ ${totalCobros.toFixed(2)}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold;">Devolución:</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-size: 16px;">S/ ${parseFloat(record.devolucion || 0).toFixed(2)}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold;">Incremento de Caja Chica (Neto):</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; color: ${totalAjustesEfectivoNeto >= 0 ? '#3f8600' : '#cf1322'}; font-size: 16px;">
                ${totalAjustesEfectivoNeto >= 0 ? '+' : '-'} S/ ${Math.abs(totalAjustesEfectivoNeto).toFixed(2)}
              </td>
            </tr>
            <tr>
              <td style="padding: 8px; font-weight: bold;">Efectivo Disponible:</td>
              <td style="padding: 8px; color: ${efectivoDisponible >= 0 ? '#3f8600' : '#cf1322'}; font-size: 16px;">S/ ${efectivoDisponible.toFixed(2)}</td>
            </tr>
          </table>
        </div>
        ` : ''}
        
        ${record.tipo === 'tarjeta' ? `
        <div style="margin-bottom: 30px; border: 1px solid #d9d9d9; padding: 15px; border-radius: 4px;">
          <h2 style="margin: 0 0 15px 0; font-size: 18px; color: #333;">Resumen Financiero - Tarjeta</h2>
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold; width: 50%;">Tarjeta de Combustible:</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; color: #1890ff; font-size: 16px;">S/ ${parseFloat(record.tarjeta_combustible || 0).toFixed(2)}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold;">Gastos en Tarjeta:</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; color: #cf1322; font-size: 16px;">S/ ${totalGastosTarjeta.toFixed(2)}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold;">Ajustes de Saldo (Neto):</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; color: ${totalAjustesTarjetaNeto >= 0 ? '#3f8600' : '#cf1322'}; font-size: 16px;">
                ${totalAjustesTarjetaNeto >= 0 ? '+' : '-'} S/ ${Math.abs(totalAjustesTarjetaNeto).toFixed(2)}
              </td>
            </tr>
            <tr>
              <td style="padding: 8px; font-weight: bold;">Tarjeta Disponible:</td>
              <td style="padding: 8px; color: ${tarjetaDisponible >= 0 ? '#3f8600' : '#cf1322'}; font-size: 16px;">S/ ${tarjetaDisponible.toFixed(2)}</td>
            </tr>
          </table>
        </div>
        ` : ''}

        ${record.tipo === 'efectivo' ? `
        <div style="margin-bottom: 30px; border: 1px solid #d9d9d9; padding: 15px; border-radius: 4px;">
          <h2 style="margin: 0 0 15px 0; font-size: 18px; color: #333;">Resumen de Movimientos</h2>
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold; width: 50%;">Total Gastos Efectivo:</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-size: 16px;">S/ ${totalGastosEfectivo.toFixed(2)}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold;">Total Cobros:</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-size: 16px;">S/ ${totalCobros.toFixed(2)}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold;">Ajustes Efectivo (+):</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-size: 16px; color: #3f8600;">S/ ${ajustesEfectivoAgregar.toFixed(2)}</td>
            </tr>
            <tr>
              <td style="padding: 8px; font-weight: bold;">Ajustes Efectivo (-):</td>
              <td style="padding: 8px; font-size: 16px; color: #cf1322;">S/ ${ajustesEfectivoQuitar.toFixed(2)}</td>
            </tr>
          </table>
        </div>
        ` : ''}

        ${record.tipo === 'tarjeta' ? `
        <div style="margin-bottom: 30px; border: 1px solid #d9d9d9; padding: 15px; border-radius: 4px;">
          <h2 style="margin: 0 0 15px 0; font-size: 18px; color: #333;">Resumen de Movimientos</h2>
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold; width: 50%;">Total Gastos Tarjeta:</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-size: 16px;">S/ ${totalGastosTarjeta.toFixed(2)}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-weight: bold;">Ajustes Tarjeta (+):</td>
              <td style="padding: 8px; border-bottom: 1px solid #f0f0f0; font-size: 16px; color: #3f8600;">S/ ${ajustesTarjetaAgregar.toFixed(2)}</td>
            </tr>
            <tr>
              <td style="padding: 8px; font-weight: bold;">Ajustes Tarjeta (-):</td>
              <td style="padding: 8px; font-size: 16px; color: #cf1322;">S/ ${ajustesTarjetaQuitar.toFixed(2)}</td>
            </tr>
          </table>
        </div>
        ` : ''}
        
        ${record.estado === 'liquidado' && record.tipo === 'efectivo' ? `
        <div style="margin-bottom: 30px; border: 1px solid #d9d9d9; padding: 15px; border-radius: 4px;">
          <h2 style="margin: 0 0 15px 0; font-size: 18px; color: #333;">Liquidación</h2>
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 8px; font-weight: bold; width: 50%;">Devolución:</td>
              <td style="padding: 8px; font-size: 16px;">S/ ${parseFloat(record.devolucion || 0).toFixed(2)}</td>
            </tr>
          </table>
        </div>
        ` : ''}
        
        ${record.tipo === 'efectivo' ? `
        <div style="margin-bottom: 20px;">
          <h2 style="margin: 0 0 15px 0; font-size: 18px; color: #333;">Detalle de Gastos en Efectivo</h2>
          ${gastosEfectivo.length > 0 ? `
            <table style="width: 100%; border-collapse: collapse; border: 1px solid #d9d9d9; margin-bottom: 20px;">
              <thead>
                <tr style="background-color: #fafafa;">
                  <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: left; font-size: 12px;">Fecha</th>
                  <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: left; font-size: 12px;">Tipo</th>
                  <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: right; font-size: 12px;">Monto</th>
                  <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: left; font-size: 12px;">Descripción</th>
                  <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: center; font-size: 12px;">Estado</th>
                </tr>
              </thead>
              <tbody>
                ${gastosEfectivo.map((gasto, index) => `
                  <tr style="${index % 2 === 0 ? 'background-color: #fafafa;' : ''}">
                    <td style="padding: 8px; border: 1px solid #d9d9d9; font-size: 11px;">${gasto.fecha_gasto ? dayjs(gasto.fecha_gasto).format('DD/MM/YYYY') : '-'}</td>
                    <td style="padding: 8px; border: 1px solid #d9d9d9; font-size: 11px;">${gasto.tipo_gasto ? gasto.tipo_gasto.toUpperCase() : '-'}</td>
                    <td style="padding: 8px; border: 1px solid #d9d9d9; text-align: right; font-size: 11px;">S/ ${parseFloat(gasto.monto || 0).toFixed(2)}</td>
                    <td style="padding: 8px; border: 1px solid #d9d9d9; font-size: 11px;">${gasto.descripcion || '-'}</td>
                    <td style="padding: 8px; border: 1px solid #d9d9d9; text-align: center; font-size: 11px;">
                      ${gasto.estado === 'aprobado' ? '✓ Aprobado' : gasto.estado === 'rechazado' ? '✗ Rechazado' : '⏳ Pendiente'}
                    </td>
                  </tr>
                `).join('')}
                <tr style="background-color: #f0f0f0; font-weight: bold;">
                  <td colspan="2" style="padding: 10px; border: 1px solid #d9d9d9; text-align: right; font-size: 12px;">TOTAL EFECTIVO:</td>
                  <td style="padding: 10px; border: 1px solid #d9d9d9; text-align: right; font-size: 12px;">
                    S/ ${gastosEfectivo.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0).toFixed(2)}
                  </td>
                  <td colspan="2" style="padding: 10px; border: 1px solid #d9d9d9;"></td>
                </tr>
              </tbody>
            </table>
          ` : '<p style="color: #999; font-style: italic; margin-bottom: 20px;">No hay gastos en efectivo registrados</p>'}
        </div>
        
        <div style="margin-bottom: 20px;">
          <h2 style="margin: 0 0 15px 0; font-size: 18px; color: #333;">Detalle de Cobros</h2>
          ${cobrosCaja.length > 0 ? `
            <table style="width: 100%; border-collapse: collapse; border: 1px solid #d9d9d9;">
              <thead>
                <tr style="background-color: #fafafa;">
                  <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: left; font-size: 12px;">Fecha</th>
                  <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: right; font-size: 12px;">Monto</th>
                  <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: left; font-size: 12px;">Descripción</th>
                  <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: left; font-size: 12px;">Observaciones</th>
                </tr>
              </thead>
              <tbody>
                ${cobrosCaja.map((cobro, index) => `
                  <tr style="${index % 2 === 0 ? 'background-color: #fafafa;' : ''}">
                    <td style="padding: 8px; border: 1px solid #d9d9d9; font-size: 11px;">${cobro.fecha_cobro ? dayjs(cobro.fecha_cobro).format('DD/MM/YYYY') : '-'}</td>
                    <td style="padding: 8px; border: 1px solid #d9d9d9; text-align: right; font-size: 11px;">S/ ${parseFloat(cobro.monto || 0).toFixed(2)}</td>
                    <td style="padding: 8px; border: 1px solid #d9d9d9; font-size: 11px;">${cobro.descripcion || '-'}</td>
                    <td style="padding: 8px; border: 1px solid #d9d9d9; font-size: 11px;">${cobro.observaciones || '-'}</td>
                  </tr>
                `).join('')}
                <tr style="background-color: #f0f0f0; font-weight: bold;">
                  <td colspan="1" style="padding: 10px; border: 1px solid #d9d9d9; text-align: right; font-size: 12px;">TOTAL COBROS:</td>
                  <td style="padding: 10px; border: 1px solid #d9d9d9; text-align: right; font-size: 12px;">
                    S/ ${cobrosCaja.reduce((sum, c) => sum + parseFloat(c.monto || 0), 0).toFixed(2)}
                  </td>
                  <td colspan="2" style="padding: 10px; border: 1px solid #d9d9d9;"></td>
                </tr>
              </tbody>
            </table>
          ` : '<p style="color: #999; font-style: italic;">No hay cobros registrados</p>'}
        </div>
        ` : ''}
        
        ${record.tipo === 'tarjeta' ? `
        <div style="margin-bottom: 20px;">
          <h2 style="margin: 0 0 15px 0; font-size: 18px; color: #333;">Detalle de Gastos en Tarjeta</h2>
          ${gastosTarjeta.length > 0 ? `
            <table style="width: 100%; border-collapse: collapse; border: 1px solid #d9d9d9;">
              <thead>
                <tr style="background-color: #fafafa;">
                  <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: left; font-size: 12px;">Fecha</th>
                  <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: left; font-size: 12px;">Tipo</th>
                  <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: right; font-size: 12px;">Monto</th>
                  <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: left; font-size: 12px;">Descripción</th>
                  <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: center; font-size: 12px;">Estado</th>
                </tr>
              </thead>
              <tbody>
                ${gastosTarjeta.map((gasto, index) => `
                  <tr style="${index % 2 === 0 ? 'background-color: #fafafa;' : ''}">
                    <td style="padding: 8px; border: 1px solid #d9d9d9; font-size: 11px;">${gasto.fecha_gasto ? dayjs(gasto.fecha_gasto).format('DD/MM/YYYY') : '-'}</td>
                    <td style="padding: 8px; border: 1px solid #d9d9d9; font-size: 11px;">${gasto.tipo_gasto ? gasto.tipo_gasto.toUpperCase() : '-'}</td>
                    <td style="padding: 8px; border: 1px solid #d9d9d9; text-align: right; font-size: 11px;">S/ ${parseFloat(gasto.monto || 0).toFixed(2)}</td>
                    <td style="padding: 8px; border: 1px solid #d9d9d9; font-size: 11px;">${gasto.descripcion || '-'}</td>
                    <td style="padding: 8px; border: 1px solid #d9d9d9; text-align: center; font-size: 11px;">
                      ${gasto.estado === 'aprobado' ? '✓ Aprobado' : gasto.estado === 'rechazado' ? '✗ Rechazado' : '⏳ Pendiente'}
                    </td>
                  </tr>
                `).join('')}
                <tr style="background-color: #f0f0f0; font-weight: bold;">
                  <td colspan="2" style="padding: 10px; border: 1px solid #d9d9d9; text-align: right; font-size: 12px;">TOTAL TARJETA:</td>
                  <td style="padding: 10px; border: 1px solid #d9d9d9; text-align: right; font-size: 12px;">
                    S/ ${gastosTarjeta.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0).toFixed(2)}
                  </td>
                  <td colspan="2" style="padding: 10px; border: 1px solid #d9d9d9;"></td>
                </tr>
              </tbody>
            </table>
          ` : '<p style="color: #999; font-style: italic;">No hay gastos en tarjeta registrados</p>'}
        </div>
        ` : ''}
        
        ${ajustes.length > 0 ? `
        <div style="margin-bottom: 20px;">
          <h2 style="margin: 0 0 15px 0; font-size: 18px; color: #333;">Detalle Incrementos de Caja Chica</h2>
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #d9d9d9;">
            <thead>
              <tr style="background-color: #fafafa;">
                <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: left; font-size: 12px;">Fecha</th>
                <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: left; font-size: 12px;">Usuario</th>
                <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: left; font-size: 12px;">Tipo</th>
                <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: right; font-size: 12px;">Monto</th>
                <th style="padding: 10px; border: 1px solid #d9d9d9; text-align: left; font-size: 12px;">Observaciones</th>
              </tr>
            </thead>
            <tbody>
              ${ajustes.map((ajuste, index) => `
                <tr style="${index % 2 === 0 ? 'background-color: #fafafa;' : ''}">
                  <td style="padding: 8px; border: 1px solid #d9d9d9; font-size: 11px;">${ajuste.fecha_ajuste ? dayjs(ajuste.fecha_ajuste).format('DD/MM/YYYY HH:mm') : '-'}</td>
                  <td style="padding: 8px; border: 1px solid #d9d9d9; font-size: 11px;">${ajuste.usuario_nombre || '-'}</td>
                  <td style="padding: 8px; border: 1px solid #d9d9d9; font-size: 11px;">${ajuste.tipo_ajuste === 'agregar' ? 'Agregar' : 'Quitar'}</td>
                  <td style="padding: 8px; border: 1px solid #d9d9d9; text-align: right; font-size: 11px; color: ${ajuste.tipo_ajuste === 'agregar' ? '#3f8600' : '#cf1322'};">
                    ${ajuste.tipo_ajuste === 'agregar' ? '+' : '-'} S/ ${parseFloat(ajuste.monto || 0).toFixed(2)}
                  </td>
                  <td style="padding: 8px; border: 1px solid #d9d9d9; font-size: 11px;">${ajuste.observaciones || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}
      `;

      // Esperar a que se renderice el contenido
      await new Promise(resolve => setTimeout(resolve, 500));

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210; // Ancho A4 en mm
      const pageHeight = 297; // Alto A4 en mm
      const margin = 20; // Márgenes en mm
      const usablePageHeight = pageHeight - (margin * 2); // Altura usable

      // Función para convertir píxeles a mm
      const pxToMm = (px) => (px * 25.4) / 96;

      // Función para agregar una página al PDF
      const addPageToPDF = async (contentHTML, isFirst = false) => {
        const pageDiv = document.createElement('div');
        pageDiv.style.width = '210mm';
        pageDiv.style.padding = `${margin}mm`;
        pageDiv.style.fontFamily = 'Arial, sans-serif';
        pageDiv.style.backgroundColor = '#fff';
        pageDiv.style.position = 'absolute';
        pageDiv.style.left = '-9999px';
        pageDiv.innerHTML = contentHTML;
        document.body.appendChild(pageDiv);

        await new Promise(resolve => setTimeout(resolve, 200));

        const canvas = await html2canvas(pageDiv, {
          scale: 2,
          useCORS: true,
          logging: false,
          windowWidth: pageDiv.scrollWidth,
          windowHeight: pageDiv.scrollHeight,
        });

        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const imgData = canvas.toDataURL('image/png');

        // Solo agregar nueva página si no es la primera
        if (!isFirst) {
          pdf.addPage();
        }
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

        document.body.removeChild(pageDiv);
      };

      // Obtener todas las secciones del DOM renderizado
      const header = div.querySelector('div[style*="text-align: center"]');
      // Obtener todas las secciones EXCEPTO el header
      const allSections = Array.from(div.querySelectorAll('div[style*="margin-bottom"]'));
      const sections = allSections.filter(section => section !== header);

      let currentPageContent = [];
      let currentHeightMM = 0;
      let isFirstPage = true;
      let headerAdded = false;

      // Agregar header solo a la primera página
      if (header && !headerAdded) {
        const headerHTML = header.outerHTML;
        currentPageContent.push(headerHTML);
        currentHeightMM = pxToMm(header.offsetHeight);
        headerAdded = true;
      }

      // Procesar cada sección
      for (const section of sections) {
        const sectionHTML = section.outerHTML;
        const sectionHeightMM = pxToMm(section.offsetHeight);

        // Calcular altura total si agregamos esta sección
        const totalHeightWithSection = currentHeightMM + sectionHeightMM;

        // Verificar si agregar esta sección excedería la página
        // Solo crear nueva página si ya tenemos contenido (header o al menos una sección)
        const hasContent = currentPageContent.length > 0;
        if (totalHeightWithSection > usablePageHeight && hasContent) {
          // Guardar página actual y empezar nueva (sin header)
          await addPageToPDF(currentPageContent.join(''), isFirstPage);
          currentPageContent = [sectionHTML];
          currentHeightMM = sectionHeightMM;
          isFirstPage = false;
        } else {
          // Agregar sección a la página actual
          currentPageContent.push(sectionHTML);
          currentHeightMM = totalHeightWithSection;
        }
      }

      // Agregar última página si hay contenido
      if (currentPageContent.length > 0) {
        await addPageToPDF(currentPageContent.join(''), isFirstPage);
      }

      const fileName = `Resumen_Caja_Chica_${record.personal_nombre?.replace(/\s+/g, '_') || 'Operario'}_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`;
      pdf.save(fileName);

      // Limpiar
      document.body.removeChild(div);

      message.success({ content: 'PDF generado exitosamente', key: 'export' });
    } catch (error) {
      console.error('Error al generar PDF:', error);
      message.error({ content: 'Error al generar PDF', key: 'export' });
    }
  };

  const handleAprobarGasto = async (gastoId) => {
    try {
      await api.put(`/gastos-caja-chica/${gastoId}`, { estado: 'aprobado' });
      message.success('Gasto aprobado exitosamente');
      fetchGastos();
      fetchCajasChica();
    } catch (error) {
      message.error('Error al aprobar gasto');
    }
  };

  const handleRechazarGasto = async (gastoId) => {
    try {
      await api.put(`/gastos-caja-chica/${gastoId}`, { estado: 'rechazado' });
      message.success('Gasto rechazado exitosamente');
      fetchGastos();
      fetchCajasChica();
    } catch (error) {
      message.error('Error al rechazar gasto');
    }
  };

  const getTipoGastoColor = (tipo) => {
    const colores = {
      operativo: 'blue',
      combustible: 'orange',
      peaje: 'green',
      otro: 'default',
    };
    return colores[tipo] || 'default';
  };

  const getEstadoColor = (estado) => {
    const colores = {
      pendiente: 'orange',
      aprobado: 'green',
      rechazado: 'red',
    };
    return colores[estado] || 'default';
  };

  // Columnas para cajas chicas de efectivo
  const cajasEfectivoColumns = [
    {
      title: 'Personal',
      key: 'personal',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.personal_nombre}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            {record.personal_documento} - {record.personal_cargo}
          </div>
        </div>
      ),
    },
    {
      title: 'Monto Asignado',
      dataIndex: 'monto_asignado',
      key: 'monto_asignado',
      render: (monto) => monto ? `S/ ${parseFloat(monto).toFixed(2)}` : 'S/ 0.00',
    },
    {
      title: 'Efectivo Disponible',
      dataIndex: 'monto_disponible',
      key: 'monto_disponible',
      render: (monto) => (
        <Tag color={parseFloat(monto || 0) > 0 ? 'green' : 'red'}>
          S/ {parseFloat(monto || 0).toFixed(2)}
        </Tag>
      ),
    },
    {
      title: 'Monto Utilizado',
      key: 'monto_utilizado_efectivo',
      render: (_, record) => {
        const gastosEfectivoAprobados = gastos
          .filter(g => g.caja_chica_id === record.id &&
            g.estado === 'aprobado' &&
            (g.tipo_pago === 'efectivo' || !g.tipo_pago))
          .reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);
        return `S/ ${gastosEfectivoAprobados.toFixed(2)}`;
      },
    },
    {
      title: 'Total Cobros',
      key: 'total_cobros',
      render: (_, record) => {
        const totalCobros = cobros
          .filter(c => c.caja_chica_id === record.id)
          .reduce((sum, c) => sum + parseFloat(c.monto || 0), 0);
        return (
          <Tag color="blue">
            S/ {totalCobros.toFixed(2)}
          </Tag>
        );
      },
    },
    {
      title: 'Incremento de Caja Chica',
      key: 'ajustes_saldo',
      render: (_, record) => {
        const ajustesCaja = ajustesSaldo.filter(a => a.caja_chica_id === record.id && a.tipo_caja === 'efectivo');
        const ajustesAgregar = ajustesCaja
          .filter(a => a.tipo_ajuste === 'agregar')
          .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
        const ajustesQuitar = ajustesCaja
          .filter(a => a.tipo_ajuste === 'quitar')
          .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);

        if (ajustesAgregar === 0 && ajustesQuitar === 0) {
          return <span style={{ color: '#999' }}>-</span>;
        }

        return (
          <div>
            {ajustesAgregar > 0 && (
              <Tag color="green" style={{ marginBottom: '4px', display: 'block' }}>
                + S/ {ajustesAgregar.toFixed(2)}
              </Tag>
            )}
            {ajustesQuitar > 0 && (
              <Tag color="red">
                - S/ {ajustesQuitar.toFixed(2)}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => (
        <Tag color={estado === 'liquidado' ? 'green' : 'blue'}>
          {estado === 'liquidado' ? 'Liquidado' : 'En Curso'}
        </Tag>
      ),
    },
    {
      title: 'Fecha Apertura',
      dataIndex: 'fecha_apertura',
      key: 'fecha_apertura',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Fecha Liquidación',
      dataIndex: 'fecha_liquidacion',
      key: 'fecha_liquidacion',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Devolución',
      dataIndex: 'devolucion',
      key: 'devolucion',
      render: (monto) => monto ? `S/ ${parseFloat(monto).toFixed(2)}` : 'S/ 0.00',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      fixed: 'right',
      width: 250,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            <span className="hide-on-mobile">Observaciones</span>
          </Button>
          {isAdmin && record.estado === 'en_curso' && (
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={() => handleAjustarSaldo(record)}
              size="small"
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
            >
              Incrementar Caja Chica
            </Button>
          )}
          {isAdmin && record.estado === 'liquidado' && (
            <Button
              type="default"
              disabled
              icon={<PlusCircleOutlined />}
              size="small"
              title="No se puede ajustar saldo de una caja chica liquidada"
            >
              Incrementar Caja Chica
            </Button>
          )}
          {isAdmin && (
            <Button
              type="default"
              icon={<DollarOutlined />}
              onClick={() => handleVerHistorialAjustes(record)}
              size="small"
            >
              <span className="hide-on-mobile">Historial</span>
            </Button>
          )}
          <Popconfirm
            title="¿Estás seguro de eliminar esta asignación?"
            description="Esta acción eliminará todos los gastos y cobros asociados."
            onConfirm={() => handleDelete(record)}
            okText="Sí, eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              <span className="hide-on-mobile">Eliminar</span>
            </Button>
          </Popconfirm>
          <Button
            onClick={() => handleVerMovimientos(record)}
            size="small"
          >
            <span className="hide-on-mobile">Gastos/Cobros</span>
          </Button>
          {record.estado === 'en_curso' && (
            <Button
              type="default"
              onClick={() => handleLiquidar(record)}
              size="small"
            >
              <span className="hide-on-mobile">Liquidar</span>
            </Button>
          )}
          <Button
            type="default"
            icon={<FilePdfOutlined />}
            onClick={() => handleExportarPDF(record)}
            size="small"
          >
            <span className="hide-on-mobile">PDF</span>
          </Button>
        </Space>
      ),
    },
  ];

  // Columnas para cajas chicas de tarjeta
  const cajasTarjetaColumns = [
    {
      title: 'Personal',
      key: 'personal',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.personal_nombre}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            {record.personal_documento} - {record.personal_cargo}
          </div>
        </div>
      ),
    },
    {
      title: 'Saldo Inicial',
      dataIndex: 'tarjeta_combustible',
      key: 'tarjeta_combustible',
      render: (monto) => monto ? `S/ ${parseFloat(monto).toFixed(2)}` : 'S/ 0.00',
    },
    {
      title: 'Tarjeta Disponible',
      dataIndex: 'monto_disponible_tarjeta',
      key: 'monto_disponible_tarjeta',
      render: (monto) => (
        <Tag color={parseFloat(monto || 0) > 0 ? 'purple' : 'red'}>
          S/ {parseFloat(monto || 0).toFixed(2)}
        </Tag>
      ),
    },
    {
      title: 'Monto Utilizado',
      key: 'monto_utilizado_tarjeta',
      render: (_, record) => {
        const tarjetaCombustible = parseFloat(record.tarjeta_combustible || 0);
        const tarjetaDisponible = parseFloat(record.monto_disponible_tarjeta || 0);
        const utilizado = tarjetaCombustible - tarjetaDisponible;
        return `S/ ${utilizado.toFixed(2)}`;
      },
    },
    {
      title: 'Incremento de Caja Chica',
      key: 'ajustes_saldo_tarjeta',
      render: (_, record) => {
        const ajustesCaja = ajustesSaldo.filter(a => a.caja_chica_id === record.id && a.tipo_caja === 'tarjeta');
        const ajustesAgregar = ajustesCaja
          .filter(a => a.tipo_ajuste === 'agregar')
          .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
        const ajustesQuitar = ajustesCaja
          .filter(a => a.tipo_ajuste === 'quitar')
          .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);

        if (ajustesAgregar === 0 && ajustesQuitar === 0) {
          return <span style={{ color: '#999' }}>-</span>;
        }

        return (
          <div>
            {ajustesAgregar > 0 && (
              <Tag color="green" style={{ marginBottom: '4px', display: 'block' }}>
                + S/ {ajustesAgregar.toFixed(2)}
              </Tag>
            )}
            {ajustesQuitar > 0 && (
              <Tag color="red">
                - S/ {ajustesQuitar.toFixed(2)}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => (
        <Tag color={estado === 'liquidado' ? 'green' : 'blue'}>
          {estado === 'liquidado' ? 'Liquidado' : 'En Curso'}
        </Tag>
      ),
    },
    {
      title: 'Fecha Apertura',
      dataIndex: 'fecha_apertura',
      key: 'fecha_apertura',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Fecha Liquidación',
      dataIndex: 'fecha_liquidacion',
      key: 'fecha_liquidacion',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Devolución',
      dataIndex: 'devolucion',
      key: 'devolucion',
      render: (monto) => monto ? `S/ ${parseFloat(monto).toFixed(2)}` : 'S/ 0.00',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      fixed: 'right',
      width: 250,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            <span className="hide-on-mobile">Observaciones</span>
          </Button>
          {isAdmin && record.estado === 'en_curso' && (
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={() => handleAjustarSaldo(record)}
              size="small"
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
            >
              Ajustar Saldo
            </Button>
          )}
          {isAdmin && record.estado === 'liquidado' && (
            <Button
              type="default"
              disabled
              icon={<PlusCircleOutlined />}
              size="small"
              title="No se puede ajustar saldo de una caja chica liquidada"
            >
              Ajustar Saldo
            </Button>
          )}
          {isAdmin && (
            <Button
              type="default"
              icon={<DollarOutlined />}
              onClick={() => handleVerHistorialAjustes(record)}
              size="small"
            >
              <span className="hide-on-mobile">Historial</span>
            </Button>
          )}
          <Popconfirm
            title="¿Estás seguro de eliminar esta asignación?"
            description="Esta acción eliminará todos los gastos asociados."
            onConfirm={() => handleDelete(record)}
            okText="Sí, eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={record.estado === 'liquidado'}
              size="small"
            >
              <span className="hide-on-mobile">Eliminar</span>
            </Button>
          </Popconfirm>
          <Button
            onClick={() => handleVerMovimientos(record)}
            size="small"
          >
            <span className="hide-on-mobile">Gastos/Cobros</span>
          </Button>
          {record.estado === 'en_curso' && (
            <Button
              type="default"
              onClick={() => handleLiquidar(record)}
              size="small"
            >
              <span className="hide-on-mobile">Liquidar</span>
            </Button>
          )}
          <Button
            type="default"
            icon={<FilePdfOutlined />}
            onClick={() => handleExportarPDF(record)}
            size="small"
          >
            <span className="hide-on-mobile">PDF</span>
          </Button>
        </Space>
      ),
    },
  ];

  const gastosColumns = [
    {
      title: 'Personal',
      key: 'personal',
      render: (_, record) => record.personal_nombre || '-',
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha_gasto',
      key: 'fecha_gasto',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo_gasto',
      key: 'tipo_gasto',
      render: (tipo) => (
        <Tag color={getTipoGastoColor(tipo)}>
          {tipo ? tipo.toUpperCase() : '-'}
        </Tag>
      ),
    },
    {
      title: 'Tipo de Pago',
      dataIndex: 'tipo_pago',
      key: 'tipo_pago',
      render: (tipo) => (
        <Tag color={tipo === 'tarjeta' ? 'purple' : 'default'}>
          {tipo === 'tarjeta' ? 'Tarjeta' : 'Efectivo'}
        </Tag>
      ),
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      render: (monto) => monto ? `S/ ${parseFloat(monto).toFixed(2)}` : '-',
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      render: (text) => text || '-',
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado, record) => (
        <Space>
          <Tag color={getEstadoColor(estado)}>
            {estado === 'cancelado' ? 'ANULADO' : (estado ? estado.toUpperCase() : 'PENDIENTE')}
          </Tag>
          {estado === 'pendiente' && (
            <>
              <Button
                type="link"
                icon={<CheckOutlined />}
                onClick={() => handleAprobarGasto(record.id)}
              >
                Aprobar
              </Button>
              <Button
                type="link"
                danger
                icon={<CloseOutlined />}
                onClick={() => handleRechazarGasto(record.id)}
              >
                Rechazar
              </Button>
            </>
          )}
        </Space>
      ),
    },
    {
      title: 'Comprobantes',
      key: 'comprobante',
      render: (_, record) => {
        const comprobantes = parseComprobantesGasto(record.comprobante);
        if (comprobantes.length === 0) return '-';

        return (
          <Space wrap>
            {comprobantes.map((url, index) => (
              <Button
                key={`${record.id}-comp-${index}`}
                type="link"
                onClick={() => window.open(buildComprobanteGastoUrl(url), '_blank')}
              >
                Ver {comprobantes.length > 1 ? index + 1 : ''}
              </Button>
            ))}
          </Space>
        );
      },
    },
  ];

  // Obtener TODAS las cajas chicas del mismo personal (efectivo y tarjeta)
  // Gastos y cobros de la caja seleccionada (solo de esta caja, no del personal completo)
  const gastosCajaSeleccionada = selectedCaja
    ? gastos.filter(g => g.caja_chica_id === selectedCaja.id)
    : [];

  const cobrosCajaSeleccionada = selectedCaja
    ? cobros.filter(c => c.caja_chica_id === selectedCaja.id)
    : [];

  // Separar gastos de la caja seleccionada por tipo de pago
  const gastosEfectivoCaja = gastosCajaSeleccionada.filter(g => g.tipo_pago === 'efectivo' || !g.tipo_pago);
  const gastosTarjetaCaja = gastosCajaSeleccionada.filter(g => g.tipo_pago === 'tarjeta');

  // Solo contar gastos aprobados
  const gastosEfectivoAprobadosCaja = gastosEfectivoCaja.filter(g => g.estado === 'aprobado');
  const gastosTarjetaAprobadosCaja = gastosTarjetaCaja.filter(g => g.estado === 'aprobado');

  const totalGastosEfectivoCaja = gastosEfectivoAprobadosCaja.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);
  const totalGastosTarjetaCaja = gastosTarjetaAprobadosCaja.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);
  const totalGastosCaja = gastosCajaSeleccionada.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);
  const totalCobrosCaja = cobrosCajaSeleccionada.reduce((sum, c) => sum + parseFloat(c.monto || 0), 0);

  // Ajustes de la caja seleccionada (ya filtrados por caja_chica_id en handleVerMovimientos)
  const ajustesEfectivoCaja = selectedCaja && selectedCaja.tipo === 'efectivo'
    ? ajustesCaja.filter(a => a.tipo_caja === 'efectivo')
    : [];
  const ajustesTarjetaCaja = selectedCaja && selectedCaja.tipo === 'tarjeta'
    ? ajustesCaja.filter(a => a.tipo_caja === 'tarjeta')
    : [];

  const totalAjustesEfectivoAgregar = ajustesEfectivoCaja
    .filter(a => a.tipo_ajuste === 'agregar')
    .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
  const totalAjustesEfectivoQuitar = ajustesEfectivoCaja
    .filter(a => a.tipo_ajuste === 'quitar')
    .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);

  const totalAjustesTarjetaAgregar = ajustesTarjetaCaja
    .filter(a => a.tipo_ajuste === 'agregar')
    .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
  const totalAjustesTarjetaQuitar = ajustesTarjetaCaja
    .filter(a => a.tipo_ajuste === 'quitar')
    .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);

  // Calcular ajustes netos
  const totalAjustesEfectivoNetoCaja = totalAjustesEfectivoAgregar - totalAjustesEfectivoQuitar;
  const totalAjustesTarjetaNetoCaja = totalAjustesTarjetaAgregar - totalAjustesTarjetaQuitar;

  // Calcular disponibles usando datos de la caja seleccionada
  const efectivoDisponibleCaja = selectedCaja && selectedCaja.tipo === 'efectivo'
    ? parseFloat(selectedCaja.monto_asignado || 0)
    - totalGastosEfectivoCaja
    + totalCobrosCaja
    + totalAjustesEfectivoNetoCaja
    - (selectedCaja.estado === 'liquidado' ? parseFloat(selectedCaja.devolucion || 0) : 0)
    : 0;
  const tarjetaDisponibleCaja = selectedCaja && selectedCaja.tipo === 'tarjeta'
    ? parseFloat(selectedCaja.tarjeta_combustible || 0) - totalGastosTarjetaCaja + totalAjustesTarjetaNetoCaja
    : 0;

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="Cajas Chicas - Efectivo" key="1">
            <div style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingCaja(null);
                  form.resetFields();
                  form.setFieldsValue({ tipo: 'efectivo' });
                  setModalVisible(true);
                }}
              >
                Asignar Caja Chica Efectivo
              </Button>
            </div>
            <Table
              columns={cajasEfectivoColumns}
              dataSource={cajasEfectivo}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Cajas Chicas - Tarjeta" key="2">
            <div style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingCaja(null);
                  form.resetFields();
                  form.setFieldsValue({ tipo: 'tarjeta' });
                  setModalVisible(true);
                }}
              >
                Asignar Caja Chica Tarjeta
              </Button>
            </div>
            <Table
              columns={cajasTarjetaColumns}
              dataSource={cajasTarjeta}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Cajas Liquidadas - Efectivo" key="3">
            <Table
              columns={cajasEfectivoColumns}
              dataSource={cajasEfectivoLiquidadas}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Cajas Liquidadas - Tarjeta" key="4">
            <Table
              columns={cajasTarjetaColumns}
              dataSource={cajasTarjetaLiquidadas}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Todos los Gastos" key="5">
            <Table
              columns={gastosColumns}
              dataSource={gastos}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>

      <Modal
        title={editingCaja ? 'Editar Observaciones' : 'Asignar Caja Chica'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingCaja(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {!editingCaja && (
            <>
              <Form.Item
                name="tipo"
                label="Tipo de Caja Chica"
                rules={[{ required: true, message: 'Selecciona el tipo' }]}
              >
                <Select placeholder="Selecciona el tipo">
                  <Select.Option value="efectivo">Efectivo</Select.Option>
                  <Select.Option value="tarjeta">Tarjeta</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="personal_id"
                label="Personal"
                rules={[{ required: true, message: 'Selecciona el personal' }]}
              >
                <Select placeholder="Selecciona el personal">
                  {personal
                    .filter((p) => {
                      const tipo = form.getFieldValue('tipo');
                      if (!tipo) return true;
                      const tieneCajaEnCurso = cajasChica.some(
                        c => c.personal_id === p.id &&
                          c.tipo === tipo &&
                          c.estado === 'en_curso'
                      );
                      return !tieneCajaEnCurso;
                    })
                    .map((p) => (
                      <Select.Option key={p.id} value={p.id}>
                        {p.nombre_completo} - {p.cargo}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.tipo !== currentValues.tipo}
              >
                {({ getFieldValue }) => {
                  const tipo = getFieldValue('tipo');
                  if (tipo === 'efectivo') {
                    return (
                      <Form.Item
                        name="monto_asignado"
                        label="Monto a Asignar"
                        rules={[{ required: true, message: 'Ingresa el monto' }]}
                      >
                        <InputNumber
                          min={0}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="0.00"
                          prefix="S/"
                        />
                      </Form.Item>
                    );
                  } else if (tipo === 'tarjeta') {
                    return (
                      <Form.Item
                        name="tarjeta_combustible"
                        label="Saldo Inicial de Tarjeta"
                        rules={[{ required: true, message: 'Ingresa el saldo inicial' }]}
                      >
                        <InputNumber
                          min={0}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="0.00"
                          prefix="S/"
                        />
                      </Form.Item>
                    );
                  }
                  return null;
                }}
              </Form.Item>
            </>
          )}

          <Form.Item name="observaciones" label="Observaciones">
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingCaja ? 'Actualizar Observaciones' : 'Asignar'}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingCaja(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Ajustar Saldo"
        open={ajustarSaldoModalVisible}
        onCancel={() => {
          setAjustarSaldoModalVisible(false);
          ajustarSaldoForm.resetFields();
          setCajaAjustar(null);
        }}
        footer={null}
        width={500}
      >
        {cajaAjustar && (
          <div style={{ marginBottom: 16 }}>
            <p><strong>Personal:</strong> {cajaAjustar.personal_nombre}</p>
            <p><strong>Tipo:</strong> {cajaAjustar.tipo === 'efectivo' ? 'Efectivo' : 'Tarjeta'}</p>
            <p><strong>Saldo Actual:</strong> {
              cajaAjustar.tipo === 'efectivo'
                ? `S/ ${parseFloat(cajaAjustar.monto_disponible || 0).toFixed(2)}`
                : `S/ ${parseFloat(cajaAjustar.monto_disponible_tarjeta || 0).toFixed(2)}`
            }</p>
          </div>
        )}
        <Form form={ajustarSaldoForm} layout="vertical" onFinish={handleAjustarSaldoSubmit}>
          <Form.Item
            name="monto"
            label="Monto a Agregar/Quitar (positivo para agregar, negativo para quitar)"
            rules={[{ required: true, message: 'Ingresa el monto' }]}
          >
            <InputNumber
              precision={2}
              style={{ width: '100%' }}
              placeholder="0.00"
              prefix="S/"
            />
          </Form.Item>

          <Form.Item
            name="observaciones"
            label="Observaciones (opcional)"
          >
            <TextArea rows={3} placeholder="Motivo del ajuste de saldo..." />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Ajustar
              </Button>
              <Button
                onClick={() => {
                  setAjustarSaldoModalVisible(false);
                  ajustarSaldoForm.resetFields();
                  setCajaAjustar(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Historial Incrementos - ${cajaAjustar?.personal_nombre || ''}`}
        open={historialAjustesModalVisible}
        onCancel={() => {
          setHistorialAjustesModalVisible(false);
          setHistorialAjustes([]);
          setCajaAjustar(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setHistorialAjustesModalVisible(false);
              setHistorialAjustes([]);
              setCajaAjustar(null);
            }}
          >
            Cerrar
          </Button>
        ]}
        width={900}
      >
        {cajaAjustar && (
          <div style={{ marginBottom: 16 }}>
            <p><strong>Tipo de Caja Chica:</strong> {cajaAjustar.tipo === 'efectivo' ? 'Efectivo' : 'Tarjeta'}</p>
            <p><strong>Saldo Actual:</strong> {
              cajaAjustar.tipo === 'efectivo'
                ? `S/ ${parseFloat(cajaAjustar.monto_disponible || 0).toFixed(2)}`
                : `S/ ${parseFloat(cajaAjustar.monto_disponible_tarjeta || 0).toFixed(2)}`
            }</p>
          </div>
        )}
        <Table
          columns={[
            {
              title: 'Fecha',
              dataIndex: 'fecha_ajuste',
              key: 'fecha_ajuste',
              render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY HH:mm') : '-',
            },
            {
              title: 'Usuario',
              key: 'usuario',
              render: (_, record) => (
                <div>
                  <div style={{ fontWeight: 'bold' }}>{record.usuario_nombre || '-'}</div>
                  <div style={{ fontSize: '12px', color: '#999' }}>{record.usuario_email || '-'}</div>
                </div>
              ),
            },
            {
              title: 'Tipo de Ajuste',
              dataIndex: 'tipo_ajuste',
              key: 'tipo_ajuste',
              render: (tipo) => (
                <Tag color={tipo === 'agregar' ? 'green' : 'red'}>
                  {tipo === 'agregar' ? 'Agregar' : 'Quitar'}
                </Tag>
              ),
            },
            {
              title: 'Monto',
              dataIndex: 'monto',
              key: 'monto',
              render: (monto, record) => (
                <Tag color={record.tipo_ajuste === 'agregar' ? 'green' : 'red'}>
                  {record.tipo_ajuste === 'agregar' ? '+' : '-'} S/ {parseFloat(monto || 0).toFixed(2)}
                </Tag>
              ),
            },
            {
              title: 'Saldo Anterior',
              dataIndex: 'saldo_anterior',
              key: 'saldo_anterior',
              render: (monto) => `S/ ${parseFloat(monto || 0).toFixed(2)}`,
            },
            {
              title: 'Saldo Nuevo',
              dataIndex: 'saldo_nuevo',
              key: 'saldo_nuevo',
              render: (monto) => (
                <Tag color="blue">
                  S/ {parseFloat(monto || 0).toFixed(2)}
                </Tag>
              ),
            },
            {
              title: 'Observaciones',
              dataIndex: 'observaciones',
              key: 'observaciones',
              render: (text) => text || '-',
            },
          ]}
          dataSource={historialAjustes}
          rowKey="id"
          loading={loadingHistorial}
          pagination={{ pageSize: 10 }}
        />
      </Modal>

      <Modal
        title={`Movimientos - ${selectedCaja?.personal_nombre || ''} (${selectedCaja?.tipo === 'tarjeta' ? 'Tarjeta' : 'Efectivo'})`}
        open={movimientosModalVisible}
        onCancel={() => {
          setMovimientosModalVisible(false);
          setSelectedCaja(null);
          setAjustesCaja([]);
        }}
        footer={[
          <Button
            key="pdf"
            type="primary"
            icon={<FilePdfOutlined />}
            onClick={() => {
              if (selectedCaja) {
                handleExportarPDF(selectedCaja);
              }
            }}
          >
            Exportar PDF
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setMovimientosModalVisible(false);
              setSelectedCaja(null);
              setAjustesCaja([]);
            }}
          >
            Cerrar
          </Button>
        ]}
        width={1000}
      >
        {selectedCaja && (
          <Descriptions bordered column={3} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Estado">
              <Tag color={selectedCaja.estado === 'liquidado' ? 'green' : 'blue'}>
                {selectedCaja.estado === 'liquidado' ? 'Liquidado' : 'En Curso'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Fecha Apertura">
              {selectedCaja.fecha_apertura ? dayjs(selectedCaja.fecha_apertura).format('DD/MM/YYYY') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha Liquidación">
              {selectedCaja.fecha_liquidacion ? dayjs(selectedCaja.fecha_liquidacion).format('DD/MM/YYYY') : '-'}
            </Descriptions.Item>
            {selectedCaja.tipo === 'efectivo' && (
              <>
                <Descriptions.Item label="Monto Asignado">
                  S/ {parseFloat(selectedCaja.monto_asignado || 0).toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Efectivo Disponible">
                  <Tag color={parseFloat(selectedCaja.monto_disponible || 0) > 0 ? 'green' : 'red'}>
                    S/ {parseFloat(selectedCaja.monto_disponible || 0).toFixed(2)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Devolución">
                  S/ {parseFloat(selectedCaja.devolucion || 0).toFixed(2)}
                </Descriptions.Item>
              </>
            )}
            {selectedCaja.tipo === 'tarjeta' && (
              <>
                <Descriptions.Item label="Saldo Inicial Tarjeta">
                  S/ {parseFloat(selectedCaja.tarjeta_combustible || 0).toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Tarjeta Disponible">
                  <Tag color={parseFloat(selectedCaja.monto_disponible_tarjeta || 0) > 0 ? 'purple' : 'red'}>
                    S/ {parseFloat(selectedCaja.monto_disponible_tarjeta || 0).toFixed(2)}
                  </Tag>
                </Descriptions.Item>
              </>
            )}
          </Descriptions>
        )}

        {selectedCaja && selectedCaja.tipo === 'efectivo' && (
          <Card title="Resumen Financiero - Efectivo" style={{ marginBottom: 16 }}>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Monto Asignado">
                <Tag color="blue">S/ {parseFloat(selectedCaja.monto_asignado || 0).toFixed(2)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Gastos en Efectivo">
                <Tag color="red">S/ {totalGastosEfectivoCaja.toFixed(2)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Cobros">
                <Tag color="blue">S/ {totalCobrosCaja.toFixed(2)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Devolución">
                <Tag color="default">S/ {parseFloat(selectedCaja.devolucion || 0).toFixed(2)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Ajustes de Saldo (Neto)">
                <Tag color={totalAjustesEfectivoNetoCaja >= 0 ? 'green' : 'red'}>
                  {totalAjustesEfectivoNetoCaja >= 0 ? '+' : '-'} S/ {Math.abs(totalAjustesEfectivoNetoCaja).toFixed(2)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Efectivo Disponible" span={2}>
                <Tag color={efectivoDisponibleCaja >= 0 ? 'green' : 'red'} style={{ fontSize: '16px', fontWeight: 'bold' }}>
                  S/ {efectivoDisponibleCaja.toFixed(2)}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {selectedCaja && selectedCaja.tipo === 'tarjeta' && (
          <Card title="Resumen Financiero - Tarjeta" style={{ marginBottom: 16 }}>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Tarjeta de Combustible">
                <Tag color="blue">S/ {parseFloat(selectedCaja.tarjeta_combustible || 0).toFixed(2)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Gastos en Tarjeta">
                <Tag color="red">S/ {totalGastosTarjetaCaja.toFixed(2)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Ajustes de Saldo (Neto)" span={2}>
                <Tag color={totalAjustesTarjetaNetoCaja >= 0 ? 'green' : 'red'}>
                  {totalAjustesTarjetaNetoCaja >= 0 ? '+' : '-'} S/ {Math.abs(totalAjustesTarjetaNetoCaja).toFixed(2)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Tarjeta Disponible" span={2}>
                <Tag color={tarjetaDisponibleCaja >= 0 ? 'purple' : 'red'} style={{ fontSize: '16px', fontWeight: 'bold' }}>
                  S/ {tarjetaDisponibleCaja.toFixed(2)}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        <Tabs defaultActiveKey="gastos">
          <Tabs.TabPane tab={`Gastos (${gastosCajaSeleccionada.length})`} key="gastos">
            <Table
              columns={gastosColumns}
              dataSource={gastosCajaSeleccionada}
              rowKey="id"
              pagination={{ pageSize: 8 }}
            />
          </Tabs.TabPane>

          {selectedCaja && selectedCaja.tipo === 'efectivo' && (
            <Tabs.TabPane tab={`Cobros (${cobrosCajaSeleccionada.length})`} key="cobros">
              <Table
                columns={[
                  {
                    title: 'Fecha',
                    dataIndex: 'fecha_cobro',
                    key: 'fecha_cobro',
                    render: (fecha) => (fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-'),
                  },
                  {
                    title: 'Monto',
                    dataIndex: 'monto',
                    key: 'monto',
                    render: (monto) => (
                      <Tag color="blue">S/ {parseFloat(monto || 0).toFixed(2)}</Tag>
                    ),
                  },
                  {
                    title: 'Descripción',
                    dataIndex: 'descripcion',
                    key: 'descripcion',
                    render: (t) => t || '-',
                  },
                  {
                    title: 'Observaciones',
                    dataIndex: 'observaciones',
                    key: 'observaciones',
                    render: (t) => t || '-',
                  },
                ]}
                dataSource={cobrosCajaSeleccionada}
                rowKey="id"
                pagination={{ pageSize: 8 }}
              />
            </Tabs.TabPane>
          )}

          <Tabs.TabPane tab={`Incrementos de Caja Chica (${ajustesCaja.length})`} key="ajustes">
            <Table
              columns={[
                {
                  title: 'Fecha',
                  dataIndex: 'fecha_ajuste',
                  key: 'fecha_ajuste',
                  render: (fecha) => (fecha ? dayjs(fecha).format('DD/MM/YYYY HH:mm') : '-'),
                },
                {
                  title: 'Usuario',
                  key: 'usuario',
                  render: (_, r) => (
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{r.usuario_nombre || '-'}</div>
                      <div style={{ fontSize: '12px', color: '#999' }}>{r.usuario_email || '-'}</div>
                    </div>
                  ),
                },
                {
                  title: 'Tipo',
                  dataIndex: 'tipo_ajuste',
                  key: 'tipo_ajuste',
                  render: (tipo) => (
                    <Tag color={tipo === 'agregar' ? 'green' : 'red'}>
                      {tipo === 'agregar' ? 'Agregar' : 'Quitar'}
                    </Tag>
                  ),
                },
                {
                  title: 'Monto',
                  dataIndex: 'monto',
                  key: 'monto',
                  render: (monto, r) => (
                    <Tag color={r.tipo_ajuste === 'agregar' ? 'green' : 'red'}>
                      {r.tipo_ajuste === 'agregar' ? '+' : '-'} S/ {parseFloat(monto || 0).toFixed(2)}
                    </Tag>
                  ),
                },
                {
                  title: 'Saldo Anterior',
                  dataIndex: 'saldo_anterior',
                  key: 'saldo_anterior',
                  render: (m) => `S/ ${parseFloat(m || 0).toFixed(2)}`,
                },
                {
                  title: 'Saldo Nuevo',
                  dataIndex: 'saldo_nuevo',
                  key: 'saldo_nuevo',
                  render: (m) => <Tag color="blue">S/ {parseFloat(m || 0).toFixed(2)}</Tag>,
                },
                {
                  title: 'Observaciones',
                  dataIndex: 'observaciones',
                  key: 'observaciones',
                  render: (t) => t || '-',
                },
              ]}
              dataSource={ajustesCaja}
              rowKey="id"
              loading={loadingAjustesCaja}
              pagination={{ pageSize: 8 }}
            />
          </Tabs.TabPane>
        </Tabs>
      </Modal>

      <Modal
        title="Liquidar Caja Chica"
        open={liquidarModalVisible}
        onCancel={() => {
          setLiquidarModalVisible(false);
          liquidarForm.resetFields();
          setCajaLiquidar(null);
        }}
        footer={null}
        width={500}
      >
        {cajaLiquidar && (() => {
          const { efectivoDisponible, tarjetaDisponible } = calcularMontosDisponibles(cajaLiquidar);
          return (
            <div style={{ marginBottom: 16 }}>
              <p><strong>Personal:</strong> {cajaLiquidar.personal_nombre}</p>
              <p><strong>Tipo:</strong> {cajaLiquidar.tipo === 'efectivo' ? 'Efectivo' : 'Tarjeta'}</p>
              {cajaLiquidar.tipo === 'efectivo' ? (
                <>
                  <p><strong>Monto Disponible:</strong> S/ {efectivoDisponible.toFixed(2)}</p>
                  <p style={{ color: '#1890ff', fontSize: '12px' }}>
                    Si hay saldo a favor, se creará automáticamente una nueva caja chica de efectivo con ese saldo.
                  </p>
                </>
              ) : (
                <>
                  <p><strong>Monto Disponible:</strong> S/ {tarjetaDisponible.toFixed(2)}</p>
                  <p style={{ color: '#1890ff', fontSize: '12px' }}>
                    Al liquidar, se creará automáticamente una nueva caja chica de tarjeta con el saldo inicial restablecido.
                  </p>
                </>
              )}
            </div>
          );
        })()}
        <Form form={liquidarForm} layout="vertical" onFinish={handleLiquidarSubmit}>
          {cajaLiquidar && cajaLiquidar.tipo === 'efectivo' && (
            <Form.Item
              name="devolucion"
              label="Devolución"
              rules={[{ required: true, message: 'Ingresa el monto de devolución' }]}
            >
              <InputNumber
                min={0}
                precision={2}
                style={{ width: '100%' }}
                placeholder="0.00"
                prefix="S/"
              />
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Liquidar
              </Button>
              <Button
                onClick={() => {
                  setLiquidarModalVisible(false);
                  liquidarForm.resetFields();
                  setCajaLiquidar(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CajaChica;

